/**
 * ApprovalConfirmationModal - Confirmation dialog before executing proposal actions.
 *
 * Thin wrapper over the shared ConfirmationDialog primitive. Shows a summary of
 * all actions that will be performed when the user approves, including email
 * recipients for gmail_send actions.
 */

import { Mail01 } from '@untitledui/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { EmailPreviewModal } from '@/components/emails/preview/EmailPreviewModal';
import type { EmailPreviewPayload } from '@/components/emails/preview/useEmailPreview';
import { useDisplayTimezone } from '@/hooks/useDisplayTimezone';
import { Badge } from '@/lib/base/badges/badges';
import { ConfirmationDialog } from '@/lib/base/overlays';
import { formatDateTimeCompact } from '@/lib/crm/object-cards/formatDateTime';
import { useResolvedCalendarSlot } from '@/lib/proposals/hooks/useResolvedCalendarSlot';
import { useOrgTimeFormat } from '@/lib/time';

import type {
  CalendarEventCreatePayload,
  GmailSendPayload,
  ProposedAction,
} from '../../lib/proposals/core/types';
import { isCalendarEventCreatePayload } from '../../lib/proposals/core/types';
import { getActionMetadata } from '../../lib/proposals/registry/utils';
import { isGmailSendAction } from '../../lib/proposals/utils/helpers';

export interface EmailRecipients {
  to: string[];
  cc: string[];
  bcc: string[];
}

export interface ApprovalConfirmationModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Actions that will be executed */
  actions: ProposedAction[];
  /** Live email recipients (from editor, not saved payload) */
  emailRecipients?: EmailRecipients | null;
  /** Whether an operation is in progress */
  isLoading?: boolean;
  /**
   * Label for the confirm button. Callers should pass the exact same label
   * as the footer approve button that opened this modal, so the user sees
   * their choice echoed back to them (not a different label derived from
   * one kind of action).
   */
  confirmLabel: string;
  /** Callback when user confirms */
  onConfirm: () => void | Promise<void>;
  /** Callback when user cancels */
  onCancel: () => void;
  /**
   * Enables the per-email Preview control. Passed in rather than read from
   * OrgContext so this modal stays context-free — mounting the preview pulled
   * `useOrg`/`useUser` into every consumer's test tree once before, and both
   * callers already hold the id (`action.org` / `proposal.org`).
   */
  orgId?: number;
}

/**
 * Build the preview request for an email action.
 *
 * Live recipients win over the stored payload for the same reason the summary
 * row shows them: the user may have edited them since the action was saved, and
 * this is the last look before an irreversible send. Nulls are normalised to
 * undefined — the backend persists unset fields as null and echoes them back
 * (see issue #10222).
 */
function buildPreviewPayload(
  action: ProposedAction,
  liveRecipients?: EmailRecipients | null
): EmailPreviewPayload {
  const params = (action.payload as GmailSendPayload).action_params ?? {};
  const cc = liveRecipients?.cc ?? params.cc ?? [];
  const bcc = liveRecipients?.bcc ?? params.bcc ?? [];
  return {
    to: liveRecipients?.to ?? params.to ?? [],
    cc: cc.length > 0 ? cc : undefined,
    bcc: bcc.length > 0 ? bcc : undefined,
    subject: params.subject ?? '',
    body: params.body ?? '',
    body_html: params.body_html ?? undefined,
    social_account_id: params.social_account_id ?? undefined,
    reply_to_message_id: params.reply_to_message_id ?? undefined,
    thread_id: params.thread_id ?? undefined,
    // The stored payload's typed disposition — keeps this last-look preview
    // in agreement with what the send will actually do (a user_removed draft
    // must not preview with a signature).
    signature_disposition: params.signature_disposition ?? undefined,
  };
}

/** Whether a calendar action will actually send an invite (has attendees). */
function isCalendarInvite(action: ProposedAction): boolean {
  if (!isCalendarEventCreatePayload(action.payload)) {
    return false;
  }
  const { attendees } =
    (action.payload as CalendarEventCreatePayload).action_params ?? {};
  return (attendees?.length ?? 0) > 0;
}

/**
 * Explain to the user why the confirmation modal is appearing. The modal is
 * only shown for actions with external side effects (emails / calendar
 * invites) that can't be undone, so the copy calls that out explicitly.
 */
function buildReasonText(actions: ProposedAction[]): string {
  const hasEmail = actions.some(isGmailSendAction);
  const hasInvite = actions.some(isCalendarInvite);

  if (hasEmail && hasInvite) {
    return "Emails and calendar invites go out the moment you approve and can't be taken back. Take one last look before confirming.";
  }
  if (hasEmail) {
    return "Emails go out the moment you approve and can't be taken back. Take one last look before confirming.";
  }
  if (hasInvite) {
    return "Calendar invites go out the moment you approve and can't be taken back. Take one last look before confirming.";
  }
  // Defensive fallback — callers only open the modal when at least one of
  // the above is true, but we still want safe copy if that changes.
  return 'Take one last look before confirming.';
}

/**
 * Title reflects what is actually being confirmed — "Ready to send" only when
 * every action in the modal is an email (so the verb matches), otherwise
 * "Ready to approve" for mixed / non-email content.
 */
function buildTitleText(actions: ProposedAction[]): string {
  if (actions.length > 0 && actions.every(isGmailSendAction)) {
    return 'Ready to send';
  }
  return 'Ready to approve';
}

/**
 * Action types that are shown individually in the modal — the only actions
 * that warrant a double-confirmation dialog. Everything else (CRM updates,
 * associations, etc.) is summarised by the approve button label and is not
 * repeated in the modal body.
 */
const PRIORITY_ACTION_TYPES = new Set(['gmail_send', 'calendar_event_create']);

/**
 * Preview control on an email summary row. Extracted so the click handler is a
 * stable per-action callback rather than an inline arrow in the row's JSX.
 */
function EmailPreviewBadge({
  action,
  onPreview,
}: {
  action: ProposedAction;
  onPreview: (action: ProposedAction) => void;
}) {
  const handlePreview = useCallback(() => {
    onPreview(action);
  }, [onPreview, action]);

  return (
    <Badge
      size='sm'
      className='shrink-0'
      onClick={handlePreview}
      aria-label='Preview email'
      title='See exactly what will be sent'
    >
      Preview
    </Badge>
  );
}

/**
 * Calendar-event branch of ActionSummaryItem, extracted so useResolvedCalendarSlot
 * can be called unconditionally (hooks may not follow conditional early returns).
 */
function CalendarEventSummaryItem({
  action,
  orgId,
}: {
  action: ProposedAction;
  orgId?: number;
}) {
  const payload = action.payload as CalendarEventCreatePayload;
  const params = payload.action_params;
  const title = params?.title ?? 'Untitled event';
  const attendees = params?.attendees ?? [];

  const resolved = useResolvedCalendarSlot(
    params?.start_time ?? null,
    params?.end_time ?? null
  );
  const displayTimezone = useDisplayTimezone();
  const { timeFormat } = useOrgTimeFormat();

  useEffect(() => {
    if (resolved.status === 'unresolved') {
      console.warn(
        '[ApprovalConfirmationModal] Calendar event payload has no time',
        { orgId }
      );
    }
  }, [resolved.status, orgId]);

  const metadata = getActionMetadata(action);
  if (!metadata) {
    return null;
  }
  const Icon = metadata.icon;

  const { date, time } = formatDateTimeCompact(
    resolved.startTime,
    displayTimezone,
    timeFormat
  );
  const timeDisplay = [date, time].filter(Boolean).join(' · ');

  return (
    <div className='flex items-center gap-3'>
      <div className='flex-shrink-0 size-10 rounded-full bg-[var(--color-bg-brand-solid)]/10 flex items-center justify-center'>
        <Icon
          className='size-5 text-[var(--color-fg-brand-primary)]'
          aria-hidden='true'
        />
      </div>
      <div className='flex-1 min-w-0'>
        <p className='text-sm font-medium text-fg-primary truncate'>{title}</p>
        {attendees.length > 0 && (
          <p className='text-sm text-fg-secondary mt-0.5 truncate'>
            {attendees.length === 1
              ? '1 attendee'
              : `${attendees.length} attendees`}
          </p>
        )}
        {resolved.status === 'resolved' && timeDisplay && (
          <p className='text-sm text-fg-secondary mt-0.5 truncate'>
            {timeDisplay}
          </p>
        )}
        {resolved.status === 'unresolved' && (
          <p className='text-xs text-[var(--color-text-warning-primary)] mt-1'>
            Meeting time not set – verify before sending.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Single action item in the summary list.
 */
function ActionSummaryItem({
  action,
  liveRecipients,
  onPreview,
  orgId,
}: {
  action: ProposedAction;
  liveRecipients?: EmailRecipients | null;
  /** Omitted when no orgId is available, which hides the Preview control. */
  onPreview?: (action: ProposedAction) => void;
  orgId?: number;
}) {
  const metadata = getActionMetadata(action);
  if (!metadata) {
    return null;
  }

  const Icon = metadata.icon;

  // For email actions, show subject and recipients inline
  if (isGmailSendAction(action)) {
    const payload = action.payload as GmailSendPayload;
    const to = liveRecipients?.to ?? payload.action_params?.to ?? [];
    const cc = liveRecipients?.cc ?? payload.action_params?.cc ?? [];
    const bcc = liveRecipients?.bcc ?? payload.action_params?.bcc ?? [];
    const subject = payload.action_params?.subject ?? 'No subject';

    return (
      <div className='flex items-center gap-3'>
        <div className='flex-shrink-0 size-10 rounded-full bg-[var(--color-bg-brand-solid)]/10 flex items-center justify-center'>
          <Mail01
            className='size-5 text-[var(--color-fg-brand-primary)]'
            aria-hidden='true'
          />
        </div>
        <div className='flex-1 min-w-0'>
          <p className='text-sm font-medium text-fg-primary truncate'>
            {subject}
          </p>
          {to.length > 0 && (
            <p className='text-sm text-fg-secondary mt-0.5 truncate'>
              To: {to.join(', ')}
            </p>
          )}
          {cc.length > 0 && (
            <p className='text-sm text-fg-secondary mt-0.5 truncate'>
              CC: {cc.join(', ')}
            </p>
          )}
          {bcc.length > 0 && (
            <p className='text-sm text-fg-secondary mt-0.5 truncate'>
              BCC: {bcc.join(', ')}
            </p>
          )}
        </div>
        {onPreview && (
          <EmailPreviewBadge action={action} onPreview={onPreview} />
        )}
      </div>
    );
  }

  // For calendar events, delegate to CalendarEventSummaryItem which resolves
  // the slot time and shows a warning when resolution fails.
  if (isCalendarEventCreatePayload(action.payload)) {
    return <CalendarEventSummaryItem action={action} orgId={orgId} />;
  }

  // Default: show action title
  return (
    <div className='flex items-center gap-3'>
      <div className='flex-shrink-0 size-10 rounded-full bg-[var(--color-bg-brand-solid)]/10 flex items-center justify-center'>
        <Icon
          className='size-5 text-[var(--color-fg-brand-primary)]'
          aria-hidden='true'
        />
      </div>
      <div className='flex-1 min-w-0'>
        <p className='text-sm font-medium text-fg-primary'>{metadata.title}</p>
      </div>
    </div>
  );
}

/**
 * Modal that shows a summary of priority actions before approval.
 *
 * Only emails and calendar invites with attendees are shown individually —
 * those are the only actions that warrant a double-confirmation dialog
 * (irreversible external side effects). Other actions (CRM updates,
 * associations, etc.) are intentionally omitted from the modal body and
 * are summarised by the approve button label instead. The modal should
 * not be opened when there are no priority actions.
 */
export function ApprovalConfirmationModal({
  isOpen,
  actions,
  emailRecipients,
  isLoading = false,
  confirmLabel,
  onConfirm,
  onCancel,
  orgId,
}: ApprovalConfirmationModalProps) {
  const title = buildTitleText(actions);
  const description = buildReasonText(actions);

  // Mounted only once a preview has been requested. The epic's own recovery
  // notes record EmailPreviewModal being mounted unconditionally and crashing
  // 55 tests via its context hooks — keeping it lazy avoids re-importing that
  // problem into every consumer of this dialog.
  const [previewAction, setPreviewAction] = useState<ProposedAction | null>(
    null
  );
  const handlePreview = useCallback((action: ProposedAction) => {
    setPreviewAction(action);
  }, []);
  const handlePreviewOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setPreviewAction(null);
    }
  }, []);

  const previewPayload = useMemo(
    () =>
      previewAction
        ? buildPreviewPayload(previewAction, emailRecipients)
        : null,
    [previewAction, emailRecipients]
  );

  const priorityActions = useMemo(
    () =>
      actions.filter(action =>
        PRIORITY_ACTION_TYPES.has(action.proposed_action_type_name)
      ),
    [actions]
  );

  return (
    <>
      <ConfirmationDialog
        isOpen={isOpen}
        title={title}
        description={description}
        confirmLabel={confirmLabel}
        cancelLabel='Cancel'
        variant='default'
        isLoading={isLoading}
        onConfirm={onConfirm}
        onCancel={onCancel}
      >
        <div className='space-y-3 py-2'>
          {priorityActions.map(action => (
            <div
              key={action.id}
              className='p-3 bg-bg-secondary rounded-xl border border-[var(--color-border-secondary)]'
            >
              <ActionSummaryItem
                action={action}
                liveRecipients={emailRecipients}
                onPreview={orgId === undefined ? undefined : handlePreview}
                orgId={orgId}
              />
            </div>
          ))}
        </div>
      </ConfirmationDialog>
      {/* SIBLING of the dialog, never a child: the confirmation dialog's
          enter/exit animations apply CSS transforms, and a transformed
          ancestor becomes the containing block for the preview overlay's
          `fixed inset-0` positioning — collapsing the nested modal to zero
          width (blank preview) and breaking its centering. Mirrors the
          EmailActionPreview mounting, where the preview renders correctly. */}
      {orgId !== undefined && previewPayload && (
        <EmailPreviewModal
          isOpen
          onOpenChange={handlePreviewOpenChange}
          orgId={orgId}
          payload={previewPayload}
        />
      )}
    </>
  );
}
